import type { AccessToken } from '@taler/api-client'
import { isExpired } from '@taler/api-client'
import type { Clock } from '@taler/domain'

export type AuthEvent = 'signed-in' | 'signed-out'

export interface TokenStoreOptions {
  readonly clock: Clock
  /** Announces sign-in and sign-out to the app's other tabs. */
  readonly broadcast?: (event: AuthEvent) => void
  readonly skewSeconds?: bigint
}

export interface TokenStore {
  read(): AccessToken | null
  /** Null when there is no token, or when it is inside the clock-skew margin of expiry. */
  readUsable(): AccessToken | null
  write(token: AccessToken): void
  clear(options?: { readonly announce?: boolean }): void
  subscribe(listener: (token: AccessToken | null) => void): () => void
}

/**
 * In memory only. A token in `localStorage` is readable by any script that reaches the page
 * and outlives the tab that obtained it; keeping it here bounds both. The cost is that a
 * reload must renew, which is the flow's job anyway.
 */
export function createTokenStore(options: TokenStoreOptions): TokenStore {
  const skew = options.skewSeconds ?? 60n
  const listeners = new Set<(token: AccessToken | null) => void>()
  let token: AccessToken | null = null

  const announce = (next: AccessToken | null): void => {
    for (const listener of listeners) listener(next)
  }

  return {
    read: () => token,
    readUsable: () => (token !== null && !isExpired(token, options.clock.now(), skew) ? token : null),
    write(next) {
      token = next
      options.broadcast?.('signed-in')
      announce(next)
    },
    clear(clearOptions) {
      token = null
      if (clearOptions?.announce !== false) options.broadcast?.('signed-out')
      announce(null)
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
  }
}
